// 滞在カードに出すレシート行
//
// 店名は滞在カードの場所名と重なるので、何を買ったかを並べる。
// タップするとレシート画面の詳細へ飛ぶ。
// レシートの無いカード利用は、加盟店名と金額だけを控えめに並べる。
// そのカード利用をタップすると、レシートをもらえなかった分として手で登録できる。
import { CreditCard, ReceiptText } from 'lucide-react'
import { useNavigate } from 'react-router'

import type { CardTransaction } from '@/api/types/card-transaction'
import type { ReceiptSummary } from '@/api/types/receipt'
import { formatSpendingTime } from '@/lib/receipt-visit-utils'
import { RECEIPT_RETURN_STATE } from '@/pages/receipts/receipt-query'
import { PATHS } from '@/routes/paths'

const formatAmount = (amount: number | null): string => (amount === null ? '金額不明' : `¥${amount.toLocaleString()}`)

const formatItemNames = (receipt: ReceiptSummary): string => {
  if (receipt.item_names.length === 0) return receipt.store_name || '明細なし'
  return receipt.item_names.join('、')
}

type Props = {
  receipts?: ReceiptSummary[]
  cardTransactions?: CardTransaction[]
  /** レシートの無いカード利用から、手で登録するシートを開く */
  onCardTransactionClick?: (tx: CardTransaction) => void
}

export const VisitReceiptList = ({ receipts = [], cardTransactions = [], onCardTransactionClick }: Props) => {
  const navigate = useNavigate()

  if (receipts.length === 0 && cardTransactions.length === 0) return null

  return (
    <div className="mt-2 space-y-1" onClick={(e) => e.stopPropagation()}>
      {receipts.map((receipt) => (
        <button
          key={receipt.id}
          type="button"
          onClick={() => navigate(`${PATHS.RECEIPTS.LIST}?id=${receipt.id}`, { state: RECEIPT_RETURN_STATE })}
          className="flex w-full items-start gap-1.5 rounded bg-amber-500/10 px-2 py-1 text-left text-xs hover:bg-amber-500/20"
        >
          <ReceiptText className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          <span className="min-w-0 flex-1 wrap-break-word">{formatItemNames(receipt)}</span>
          <span className="shrink-0 tabular-nums">{formatAmount(receipt.total_amount)}</span>
        </button>
      ))}
      {cardTransactions.map((tx) => (
        <button
          key={`card-${tx.id}`}
          type="button"
          disabled={!onCardTransactionClick}
          onClick={() => onCardTransactionClick?.(tx)}
          className="flex w-full items-start gap-1.5 rounded border border-dashed border-amber-500/40 px-2 py-1 text-left text-xs text-muted-foreground enabled:hover:bg-amber-500/10"
        >
          <CreditCard className="mt-0.5 size-3.5 shrink-0 text-amber-600" />
          <span className="shrink-0 tabular-nums">{formatSpendingTime(tx.used_at)}</span>
          <span className="min-w-0 flex-1 wrap-break-word">
            {tx.merchant}
            <span className="ml-1 text-[10px]">（レシート未登録）</span>
          </span>
          <span className="shrink-0 tabular-nums">{formatAmount(tx.amount)}</span>
        </button>
      ))}
    </div>
  )
}
